import { useCallback, useMemo } from 'react';

import { parseNumber } from './parse';
import { HistoryMode, useUrlPatch } from './useUrlPatch';

interface UrlNumberOptions {
  /** Minimum accepted value; below it falls back to the default. */
  min?: number;
  /** Also drop the `page` param on change (e.g. pageSize resets to page 1). */
  resetPage?: boolean;
  /** The page param `resetPage` drops (default `page`; e.g. `zpage`). */
  pageKey?: string;
  history?: HistoryMode;
}

/**
 * Integer param (page, pageSize). Non-numeric / below-min values fall back.
 * Writing the fallback deletes the param.
 *
 * Note: this hook does NOT reset page on change — it typically IS the page
 * param. Sibling filter/tab/search hooks own the page-reset via `resetPage`.
 */
export function useUrlNumber(
  key: string,
  fallback: number,
  options: UrlNumberOptions = {}
): [number, (value: number) => void] {
  const {
    min = Number.NEGATIVE_INFINITY,
    resetPage = false,
    pageKey = 'page',
    history = 'replace',
  } = options;
  const { searchParams, patch } = useUrlPatch();

  const raw = searchParams.get(key);
  const value = useMemo(
    () => parseNumber(raw, fallback, min),
    [raw, fallback, min]
  );

  const setValue = useCallback(
    (next: number) => {
      patch((params) => {
        if (next === fallback) params.delete(key);
        else params.set(key, String(next));
        if (resetPage) params.delete(pageKey);
      }, history);
    },
    [patch, key, fallback, resetPage, pageKey, history]
  );

  return [value, setValue];
}
